import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Edit3, Eye, EyeOff, Plus, RefreshCw, Trash2, UtensilsCrossed } from 'lucide-react';
import clsx from 'clsx';

import PageHeader from '@/components/PageHeader';
import Modal from '@/components/Modal';
import ImageUploadField from '@/components/ImageUploadField';
import {
  createAppCuisine,
  deleteAppCuisine,
  listAppCuisines,
  mediaUrl,
  updateAppCuisine,
  type AppCuisine,
  type AppCuisineInput,
} from '@/lib/operatorApi';

// Кухни на главной гостевого приложения (Бургеры, Пицца, Суши…) — карусель
// с картинками под баннерами и фильтр ленты заведений. Каким заведениям
// какая кухня — отмечается в карточке арендатора («Кухни в приложении»).

type EditMode = { kind: 'create' } | { kind: 'edit'; cuisine: AppCuisine } | null;

export default function AppCuisines() {
  const [items, setItems] = useState<AppCuisine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditMode>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listAppCuisines()
      .then((r) => setItems(r.cuisines))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (id: string, fn: () => Promise<unknown>) => {
    try {
      setBusyId(id);
      await fn();
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (c: AppCuisine) =>
    run(c.id, () => updateAppCuisine(c.id, { isActive: !c.isActive }));

  const remove = (c: AppCuisine) => {
    const extra = c.establishmentsCount > 0
      ? ` Она отмечена у ${c.establishmentsCount} заведений — метка снимется.`
      : '';
    if (!window.confirm(`Удалить кухню «${c.name}»?${extra}`)) return;
    void run(c.id, () => deleteAppCuisine(c.id));
  };

  const move = async (index: number, dir: -1 | 1) => {
    const other = index + dir;
    if (other < 0 || other >= items.length) return;
    const a = items[index];
    const b = items[other];
    setBusyId(a.id);
    try {
      if (a.sortOrder === b.sortOrder) {
        const reordered = [...items];
        [reordered[index], reordered[other]] = [reordered[other], reordered[index]];
        await Promise.all(reordered.map((x, i) => updateAppCuisine(x.id, { sortOrder: i })));
      } else {
        await Promise.all([
          updateAppCuisine(a.id, { sortOrder: b.sortOrder }),
          updateAppCuisine(b.id, { sortOrder: a.sortOrder }),
        ]);
      }
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Гостевое приложение"
        title="Кухни"
        description="Категории с картинками на главной приложения People. По тапу гость видит только заведения с этой кухней. Какие кухни у заведения — отмечается в карточке арендатора."
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
              <RefreshCw size={13} strokeWidth={1.75} />
              Обновить
            </button>
            <button type="button" className="btn-primary" onClick={() => setEdit({ kind: 'create' })}>
              <Plus size={13} strokeWidth={1.75} />
              Новая кухня
            </button>
          </div>
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-2 text-[13px] text-red-700">
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="text-ink/50">Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="card p-10 text-center text-[14px] text-ink/50">
            Кухонь пока нет. Добавьте первые — Бургеры, Пицца, Суши, Кофе, Шаурма…
          </div>
        ) : (
          <div className="card table-scroll">
            <table className="table-grid">
              <thead>
                <tr>
                  <th className="w-16" />
                  <th>Название</th>
                  <th>Slug</th>
                  <th className="text-right">Заведений</th>
                  <th className="text-right">Порядок</th>
                  <th>Статус</th>
                  <th className="text-right" />
                </tr>
              </thead>
              <tbody>
                {items.map((c, i) => {
                  const busy = busyId === c.id;
                  const src = mediaUrl(c.imageUrl);
                  return (
                    <tr key={c.id} className={clsx(busy && 'opacity-60')}>
                      <td>
                        <div className="grid h-11 w-11 place-items-center overflow-hidden bg-paper">
                          {src ? (
                            <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
                          ) : (
                            <UtensilsCrossed size={16} strokeWidth={1.5} className="text-ink/30" />
                          )}
                        </div>
                      </td>
                      <td className="font-medium">{c.name}</td>
                      <td className="font-mono text-[12px] text-ink/60">{c.slug}</td>
                      <td className="text-right tabular-nums">{c.establishmentsCount}</td>
                      <td className="text-right tabular-nums text-ink/60">{c.sortOrder}</td>
                      <td>
                        <span className={c.isActive ? 'badge-green' : 'badge-ink'}>
                          {c.isActive ? 'Активна' : 'Скрыта'}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            className="btn-ghost !p-2"
                            title="Выше"
                            onClick={() => void move(i, -1)}
                            disabled={busy || i === 0}
                          >
                            <ArrowUp size={14} strokeWidth={1.75} />
                          </button>
                          <button
                            type="button"
                            className="btn-ghost !p-2"
                            title="Ниже"
                            onClick={() => void move(i, 1)}
                            disabled={busy || i === items.length - 1}
                          >
                            <ArrowDown size={14} strokeWidth={1.75} />
                          </button>
                          <button
                            type="button"
                            className="btn-ghost !p-2"
                            title={c.isActive ? 'Скрыть' : 'Показать'}
                            onClick={() => void toggleActive(c)}
                            disabled={busy}
                          >
                            {c.isActive ? <Eye size={14} strokeWidth={1.75} /> : <EyeOff size={14} strokeWidth={1.75} />}
                          </button>
                          <button
                            type="button"
                            className="btn-ghost !p-2"
                            title="Редактировать"
                            onClick={() => setEdit({ kind: 'edit', cuisine: c })}
                            disabled={busy}
                          >
                            <Edit3 size={14} strokeWidth={1.75} />
                          </button>
                          <button
                            type="button"
                            className="btn-ghost !p-2 text-red-600"
                            title="Удалить"
                            onClick={() => remove(c)}
                            disabled={busy}
                          >
                            <Trash2 size={14} strokeWidth={1.75} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && (
        <CuisineModal
          mode={edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ─── Форма ────────────────────────────────────────────────────────────

function CuisineModal({
  mode,
  onClose,
  onSaved,
}: {
  mode: NonNullable<EditMode>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = mode.kind === 'edit' ? mode.cuisine : null;
  const [name, setName] = useState(editing?.name ?? '');
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [imageUrl, setImageUrl] = useState<string | null>(editing?.imageUrl ?? null);
  const [sortOrder, setSortOrder] = useState(editing?.sortOrder ?? 0);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Введите название');
      return;
    }
    const slugTrimmed = slug.trim().toLowerCase();
    if (slugTrimmed && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slugTrimmed)) {
      setError('Slug — только латиница, цифры и дефис');
      return;
    }
    const body: AppCuisineInput = {
      name: trimmed,
      imageUrl,
      sortOrder,
      isActive,
      ...(slugTrimmed ? { slug: slugTrimmed } : {}),
    };
    setSaving(true);
    try {
      if (editing) await updateAppCuisine(editing.id, body);
      else await createAppCuisine(body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? 'Кухня' : 'Новая кухня'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="eyebrow mb-2 block">Картинка</label>
          <ImageUploadField
            kind="cuisine"
            value={imageUrl}
            onChange={setImageUrl}
            aspect="square"
            hint="PNG с прозрачным фоном, ~400px"
            disabled={saving}
          />
        </div>
        <div>
          <label className="eyebrow mb-1 block">Название</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Бургеры"
            autoFocus
            disabled={saving}
          />
        </div>
        <div>
          <label className="eyebrow mb-1 block">Slug</label>
          <input
            className="input font-mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            maxLength={40}
            placeholder={editing ? '' : 'Оставьте пустым — сгенерируем из названия'}
            disabled={saving}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="eyebrow mb-1 block">Порядок</label>
            <input
              className="input"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              disabled={saving}
            />
          </div>
          <label className="flex items-end gap-2 pb-2 text-[13px]">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={saving}
              className="h-4 w-4 accent-orange"
            />
            Показывать
          </label>
        </div>

        {error && <p className="text-[13px] text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-outline" onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Сохраняем…' : editing ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
